import type { QueuedImageBackend } from './chat/queued-images.js';
import type { BinaryContent } from './types/media.js';
/**
 * HTTP Layer
 *
 * Internalized HTTP client — no dependency on @localagent/core.
 * The SDK is the only interface to the backend API.
 *
 * Behavior:
 *   - Retries transient network errors (NetworkError, TimeoutError, 5xx, 429).
 *   - On 401, calls `auth.invalidate()` then retries once with a fresh token.
 *     A second 401 propagates as APIError.
 *   - Throws APIError on any non-2xx (no fallback values).
 *   - Throws SDKError if a request needs auth but no AuthProvider was given.
 *   - Throws MissingCredentialError when a CONFIGURED AuthProvider yields null
 *     for a request that did not declare itself `unauthenticated`.
 */

import {
  APIError,
  MissingCredentialError,
  NetworkError,
  RequestCancelledError,
  SDKError,
  TimeoutError,
} from './errors.js';
import { jwtStringClaim } from './auth/decodeSessionJwt.js';
import type { AuthProvider } from './auth/types.js';
import type { WebSocketFactory } from './chat/types.js';
import type { SDKStorage } from './storage/types.js';

/**
 * The device-identity handle `HttpLayer` consults for the per-request
 * `X-Device-Id` header and the registration precondition. Attached only by
 * `createSDK` — a plain `new LocalAgentSDK(config)` passes none, and its wire
 * behaviour is byte-identical to a build without this file (sdk-source-of-truth
 * sdk ADR-009).
 */
export interface DeviceIdentityHandle {
  /** The header value for one request, or null to omit it (daemon unavailable). */
  resolveHeader(): Promise<string | null>;
  /** Await registration of the resolved id against the acting account. */
  ensureRegistered(accountId: string): Promise<void>;
}

/**
 * Per-call transport options.
 *
 * These are deliberately NOT parameters on resource methods: a resource
 * method's parameter list IS its agent-dispatchable argSchema (see
 * `cancelInFlight`'s note and `.claude/rules/keep-dispatch-sweep-in-sync.md`),
 * and transport concerns must never leak into the contract the agent sees.
 */
export interface RequestOptions {
  /**
   * Declare that this route legitimately carries NO bearer credential, so a
   * null token is expected rather than a bug.
   *
   * The set is closed and is exactly the body-authenticated special-auth
   * classes 2/3/4 (`go/internal/auth/organizations.go` `SpecialAuthRoutes`,
   * middleware-exempt via `specialAuthExempt`) plus the gateway-exempt
   * discovery/health prefixes (`authExemptPrefixes` in
   * `go/internal/api/middleware.go`). Every other route MUST send a bearer, and
   * a null token there is a client-side defect that now fails loudly instead of
   * producing an unattributable 401.
   *
   * Setting this also suppresses the 401 refresh-and-retry: invalidating a
   * cached token because a bad handoff CODE was rejected would discard a
   * perfectly good session for an unrelated reason.
   */
  unauthenticated?: boolean;
  /**
   * Send the body verbatim under this content type instead of JSON-encoding it.
   * Used by the binary routes (session image upload), where the wire body IS the
   * bytes.
   */
  rawContentType?: string;
  /**
   * Return the response body as ArrayBuffer instead of JSON/text. Used by
   * media content download.
   */
  acceptBinary?: boolean;
  /** Like `acceptBinary`, but resolve to `{ bytes, contentType }`; a response without a content type throws. */
  acceptBinaryWithType?: boolean;
  /** Extra request headers — the image routes carry the label out-of-band. */
  extraHeaders?: Record<string, string>;
  /**
   * Declare that this request is issued BY the transport itself as a
   * precondition of another request — today, the device-registration POST the
   * device identity issues from inside `executeRequest`. Setting it skips the
   * registration precondition so the POST cannot re-enter its own single-flight
   * and deadlock. It is a transport concern, deliberately not a resource-method
   * parameter, and it does NOT widen the unauthenticated set: the registration
   * POST still carries a bearer and still throws MissingCredentialError
   * without one.
   */
  internal?: boolean;
  retry?: false;
}

/**
 * What the transport may hand `fetch` as a body. `BodyInit` is not a global in
 * this package's lib set, so the accepted shapes are named here.
 */
type RawRequestBody = string | Blob | ArrayBuffer;

export interface RetryConfig {
  maxRetries?: number;
  baseDelay?: number;
  maxDelay?: number;
}

export interface SDKConfig {
  /** Base URL for the API (required — no fallback to localhost). */
  baseUrl: string;
  /** Device instance ID — sent as the X-Device-Id header on every request. */
  deviceId?: string;
  /**
   * The account an operator-pinned `deviceId` belongs to. When set, the pin
   * applies only while that account is acting; any other account gets the
   * minted per-client id, registered for it (sdk ADR-009).
   */
  deviceAccountId?: string;
  /**
   * Auth provider. Optional only because some endpoints (OpenID config,
   * OTP start, token mint) work unauthenticated. Calls to authenticated
   * endpoints will throw if this is not set.
   */
  auth?: AuthProvider;
  /** Request timeout in ms (default: 30000). */
  timeout?: number;
  /** Retry config (default: { maxRetries: 2, baseDelay: 500, maxDelay: 5000 }). Set false to disable. */
  retry?: RetryConfig | false;
  /** Custom WebSocket factory for Node.js consumers (default: browser native WebSocket). */
  webSocketFactory?: WebSocketFactory;
  /** Events and transcript rows fetched per history window page (default: 200). */
  historyPageSize?: number;
  /** Most event skeletons, and separately most transcript rows, one chat keeps loaded (default: derived from device memory). */
  historyWindowCeiling?: number;
  /**
   * Where queued messages' image bytes are kept on this device (sdk ADR-b0123).
   * Defaults to IndexedDB where the host has it; Node hosts pass
   * `FileQueuedImageBackend` from `@localagent/sdk/queued-images-node`.
   */
  queuedImageBackend?: QueuedImageBackend | null;
  /**
   * Browser-launch callback used by `sdk.shogunchop.signIn()` to open the
   * cloud login page in the user's system browser. Required if the caller
   * intends to call `signIn()` from this SDK instance.
   *
   * - **Electron main process**: pass `shell.openExternal`.
   * - **Electron renderer process**: wrap an IPC bridge that forwards to
   *   main and calls `shell.openExternal` there.
   * - **Playwright fixtures**: wrap a `BrowserContext` to drive the test.
   */
  openExternal?: (url: string) => Promise<void>;
  /**
   * Configure a CREDENTIAL HOLDER (sdk ADR-010). The
   * field's PRESENCE is the configuration — there is no mode flag. Three
   * legitimate shapes: neither this nor `auth` (a pre-auth/bootstrap SDK);
   * `auth` only (tests, e2e seeds, the auth-only lane — unchanged); `credential`
   * only (web + desktop renderer). `auth` AND `credential` together throw at
   * construction: two bearer sources is a bug, not a precedence.
   *
   * `storage` is REQUIRED when the holder is configured — a browser host that
   * forgets its durable adapter would silently degrade to non-durable.
   */
  credential?: {
    storage: SDKStorage;
    /** The browser arm's renewal source (the host's Stytch client). */
    renewSource?: () => Promise<import('./auth/authState.js').CredentialMaterial | null>;
    /** Host rotation hook — required on the browser arm. */
    adopt?: (material: import('./auth/authState.js').CredentialMaterial) => Promise<void>;
    clockIntervalMs?: number;
    /** The browser arm's refresh lead; unread on the daemon arm. */
    refreshLeadMs?: number;
    /** The one lifecycle budget. */
    budgetMs?: number;
    /** The correlating dimension on lifecycle log events. */
    realm?: string;
  };
}

export class HttpLayer {
  private readonly baseUrl: string;
  private readonly headers: Record<string, string>;
  private readonly timeout: number;
  private readonly retryConfig: Required<RetryConfig> | false;
  private auth: AuthProvider | null;
  private identity: DeviceIdentityHandle | null = null;

  // Every currently in-flight request, so a high-fan-out caller can cancel a
  // superseded BATCH at the network layer via cancelInFlight() — see there
  // for why this exists instead of a per-call AbortSignal parameter.
  private readonly inFlight = new Set<{
    url: string;
    controller: AbortController;
    cancelled: boolean;
  }>();

  constructor(config: SDKConfig) {
    if (!config.baseUrl) {
      throw new SDKError('SDKConfig.baseUrl is required (no fallback to localhost)');
    }
    this.baseUrl = config.baseUrl.replace(/\/$/, '');
    this.auth = config.auth ?? null;
    this.timeout = config.timeout ?? 30000;
    this.retryConfig =
      config.retry === false
        ? false
        : {
            maxRetries: config.retry?.maxRetries ?? 2,
            baseDelay: config.retry?.baseDelay ?? 500,
            maxDelay: config.retry?.maxDelay ?? 5000,
          };

    this.headers = config.deviceId ? { 'X-Device-Id': config.deviceId } : {};
  }

  /**
   * Attach the device-identity handle (per-request `X-Device-Id` + the
   * registration precondition). `createSDK` calls this; the plain constructor
   * never does, which is what keeps the auth-only construction shape's wire
   * behaviour identical across the cutover.
   */
  setDeviceIdentity(identity: DeviceIdentityHandle): void {
    this.identity = identity;
  }

  private transportObserver: ((status: number, err?: unknown) => Promise<void>) | null = null;

  /**
   * Attach the transport verdict feed (2xx and 401 only — see the identity
   * controller). One adjudication owner; called when a credential holder is
   * configured.
   */
  setTransportObserver(observer: ((status: number, err?: unknown) => Promise<void>) | null): void {
    this.transportObserver = observer;
  }

  /**
   * Install a credential-holder-backed bearer source AFTER construction (the
   * holder exists only when `config.credential` was configured; its provider
   * projects the phase's own material — no cache, nothing to invalidate).
   */
  setAuthProvider(provider: AuthProvider): void {
    this.auth = provider;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async get<T>(path: string, params?: Record<string, any>, opts?: RequestOptions): Promise<T> {
    return this.request<T>('GET', path, undefined, params, opts);
  }

  async post<T>(path: string, body?: unknown, opts?: RequestOptions): Promise<T> {
    return this.request<T>('POST', path, body, undefined, opts);
  }

  async put<T>(path: string, body?: unknown, opts?: RequestOptions): Promise<T> {
    return this.request<T>('PUT', path, body, undefined, opts);
  }

  async patch<T>(path: string, body?: unknown, opts?: RequestOptions): Promise<T> {
    return this.request<T>('PATCH', path, body, undefined, opts);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async delete<T>(path: string, params?: Record<string, any>, opts?: RequestOptions): Promise<T> {
    return this.request<T>('DELETE', path, undefined, params, opts);
  }

  async deleteWithBody<T>(path: string, body: unknown, opts?: RequestOptions): Promise<T> {
    return this.request<T>('DELETE', path, body, undefined, opts);
  }

  /**
   * POST a raw byte body under an explicit content type. The bytes are sent
   * verbatim — no JSON encoding, no base64 — because the route reads the body as
   * the payload itself.
   */
  async postBinary<T>(
    path: string,
    body: ArrayBuffer | Blob,
    contentType: string,
    extraHeaders?: Record<string, string>
  ): Promise<T> {
    return this.request<T>('POST', path, body, undefined, {
      rawContentType: contentType,
      ...(extraHeaders ? { extraHeaders } : {}),
    });
  }

  /**
   * Abort every in-flight request whose URL contains `pathSubstring` (or
   * every in-flight request when omitted) at the real network layer — not
   * merely a "stop waiting on the response" gate.
   *
   * This exists at the SDK level, rather than as a per-call `AbortSignal`
   * parameter on individual resource methods, deliberately: a resource
   * method's parameter list IS its agent-dispatchable argSchema
   * (`application/packages/sdk/scripts/generate-docs.ts` walks every
   * resource method's parameters to build the Shogun `platform_call`
   * contract — see `.claude/rules/keep-dispatch-sweep-in-sync.md`). Adding a
   * `signal?: AbortSignal` parameter to e.g. `SearchResource.search()` would
   * change that method's argSchema shape for a purely client-side,
   * non-serializable concern the agent never needs to see or set.
   *
   * The intended caller is a high-fan-out UI hook (e.g. the account-search
   * view, which issues one `/v1/search` request per active entity type on
   * every debounced keystroke — see `useSearch.ts`): call this with the
   * shared path BEFORE issuing the next batch so the PRIOR batch's requests
   * actually release their slot in the browser's per-origin connection pool,
   * instead of completing on their own well after the caller has moved on to
   * a newer query.
   */
  cancelInFlight(pathSubstring?: string): void {
    for (const entry of this.inFlight) {
      if (pathSubstring === undefined || entry.url.includes(pathSubstring)) {
        entry.cancelled = true;
        entry.controller.abort();
      }
    }
  }

  /**
   * The acting account id, resolved from the authenticated bearer token's
   * `account_id` claim.
   *
   * Account-scoped reads that the Shogun `platform_call` executor dispatches
   * carry NO account in `arguments` — the account id is an identity field
   * stripped from the generated argSchema, so it can only come from the
   * real session token, never the agent. Such resources call this to resolve
   * "my own account" when no explicit account was passed; callers that DO have an
   * account pass it directly and never reach here. The backend still
   * authoritatively re-checks the account gate — this only learns which account
   * the caller's own token is scoped to.
   *
   * Throws loudly when unauthenticated, when the token cannot be decoded
   * (`MalformedJwtError`), or when the claim is absent (no-fallbacks): a missing
   * acting account is a bug, never a silent default.
   */
  async actingAccountId(): Promise<string> {
    if (this.auth === null) {
      throw new SDKError(
        'actingAccountId: SDK has no AuthProvider — cannot resolve the acting account'
      );
    }
    const token = await this.auth.getToken();
    if (!token) {
      throw new SDKError(
        'actingAccountId: no bearer token available to resolve the acting account'
      );
    }
    const accountId = jwtStringClaim(token, 'account_id');
    if (accountId === undefined || accountId === '') {
      throw new SDKError('actingAccountId: authenticated token carries no account_id claim');
    }
    return accountId;
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    params?: Record<string, unknown>,
    opts?: RequestOptions
  ): Promise<T> {
    const url = this.buildUrl(path, params);
    return this.requestWithRetries<T>(method, url, body, opts);
  }

  private async requestWithRetries<T>(
    method: string,
    url: string,
    body?: unknown,
    opts?: RequestOptions
  ): Promise<T> {
    if (this.retryConfig === false || opts?.retry === false) {
      return this.requestWithAuthRetry<T>(method, url, body, opts);
    }

    const { maxRetries, baseDelay, maxDelay } = this.retryConfig;
    let lastError: Error | undefined;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await this.requestWithAuthRetry<T>(method, url, body, opts);
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));

        if (attempt < maxRetries && this.isTransient(lastError)) {
          const delay = Math.min(
            maxDelay,
            baseDelay * Math.pow(2, attempt) + Math.random() * baseDelay * Math.pow(2, attempt)
          );
          await new Promise((resolve) => setTimeout(resolve, delay));
          continue;
        }

        throw lastError;
      }
    }

    throw lastError ?? new Error('Request failed unexpectedly');
  }

  /**
   * Single request with one auth-refresh retry on 401. The transient-retry
   * loop above wraps this — so a 5xx will retry the whole thing including
   * auth, while a 401 only triggers the inner refresh-and-retry once.
   *
   * An `unauthenticated` route is EXCLUDED from the refresh-and-retry: those
   * routes authenticate a credential in the BODY (a handoff code, an opaque
   * session token, a Discovery IST), so their 401 says nothing about the
   * cached bearer. Invalidating on one would discard a live session because a
   * user mistyped a handoff code.
   */
  private async requestWithAuthRetry<T>(
    method: string,
    url: string,
    body?: unknown,
    opts?: RequestOptions
  ): Promise<T> {
    try {
      const result = await this.executeRequest<T>(method, url, body, opts);
      if (this.transportObserver !== null && opts?.unauthenticated !== true) {
        // Awaited: the next request must observe the recovered phase.
        await this.transportObserver(200);
      }
      return result;
    } catch (error) {
      if (
        error instanceof APIError &&
        error.status === 401 &&
        this.auth !== null &&
        opts?.unauthenticated !== true
      ) {
        // The verdict feed is the ONE adjudication owner: classify, apply,
        // then the existing single retry with the (projected) fresh bearer.
        // A 5xx/timeout from THIS generic lane is never fed — a degraded
        // backend adjudicates nothing about anyone's session.
        if (this.transportObserver !== null) {
          await this.transportObserver(401, error);
        }
        await this.auth.invalidate();
        return this.executeRequest<T>(method, url, body, opts);
      }
      throw error;
    }
  }

  private async executeRequest<T>(
    method: string,
    url: string,
    body?: unknown,
    opts?: RequestOptions
  ): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': opts?.rawContentType ?? 'application/json',
      ...this.headers,
      ...(opts?.extraHeaders ?? {}),
    };

    // A CONFIGURED provider that yields no token is a client-side defect on
    // every route that is not explicitly unauthenticated. Omitting the header
    // and letting the backend answer 401 is what made a missing credential
    // indistinguishable from a dead session — the mechanism behind the
    // /desktop-auth sign-in loop. Fail here, where the cause is still known.
    //
    // `this.auth === null` (no provider at all) is a different, legitimate
    // configuration — an SDK built for pre-auth flows — and stays silent.
    if (this.auth !== null && opts?.unauthenticated !== true) {
      const token = await this.auth.getToken();
      if (token === null || token === '') {
        throw new MissingCredentialError(url);
      }
      headers['Authorization'] = `Bearer ${token}`;

      // The device identity is a precondition of every authenticated request on
      // clients that attached a handle: resolve the header (per request — never
      // frozen at construction) and guarantee the id is registered against the
      // acting account before the request leaves (sdk ADR-009). The header is read AFTER registration so the registration POST
      // itself carries the same identity path; a null answer (daemon unavailable)
      // omits the header for this request without failing it. Unauthent-
      // icated routes and internal (transport-issued) requests skip the
      // precondition entirely.
      if (this.identity !== null && opts?.internal !== true) {
        const accountId = jwtStringClaim(token, 'account_id');
        if (accountId !== undefined && accountId !== '') {
          await this.identity.ensureRegistered(accountId);
        }
        const deviceHeader = await this.identity.resolveHeader();
        if (deviceHeader !== null) {
          headers['X-Device-Id'] = deviceHeader;
        }
      }
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);
    const inFlightEntry = { url, controller, cancelled: false };
    this.inFlight.add(inFlightEntry);

    try {
      const response = await fetch(url, {
        method,
        headers,
        body: this.encodeBody(body, opts),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (response.status === 204) {
        return undefined as T;
      }

      const contentType = response.headers.get('content-type');
      let data: unknown;

      if (!response.ok && contentType?.includes('application/json')) {
        data = await response.json();
      } else if (opts?.acceptBinaryWithType === true && response.ok) {
        if (!contentType) {
          throw new SDKError(`${method} ${url}: binary response carried no content type`);
        }
        data = { bytes: await response.arrayBuffer(), contentType } satisfies BinaryContent;
      } else if (opts?.acceptBinary === true) {
        data = await response.arrayBuffer();
      } else if (contentType?.includes('application/json')) {
        data = await response.json();
      } else {
        data = await response.text();
      }

      if (!response.ok) {
        throw new APIError(response.status, response.statusText, data);
      }

      return data as T;
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof SDKError) {
        throw error;
      }

      // A deliberate cancelInFlight() call, not a slow backend — propagate
      // immediately as non-transient (see isTransient) so the retry loop
      // above does NOT re-issue a request the caller already superseded.
      if (inFlightEntry.cancelled) {
        throw new RequestCancelledError(url);
      }

      if (error instanceof Error && error.name === 'AbortError') {
        throw new TimeoutError(this.timeout, url, error);
      }

      if (error instanceof TypeError) {
        throw new NetworkError(error.message || 'Network request failed', url, error);
      }

      if (error instanceof Error) {
        throw new NetworkError(error.message, url, error);
      }

      throw new NetworkError('Unknown error', url);
    } finally {
      this.inFlight.delete(inFlightEntry);
    }
  }

  private encodeBody(body: unknown, opts?: RequestOptions): RawRequestBody | undefined {
    if (body === undefined) return undefined;
    if (opts?.rawContentType !== undefined) return body as RawRequestBody;
    return JSON.stringify(body);
  }

  private buildUrl(path: string, params?: Record<string, unknown>): string {
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    let url = `${this.baseUrl}${cleanPath}`;

    if (params) {
      const filtered: Record<string, string> = {};
      for (const [key, value] of Object.entries(params)) {
        if (
          value !== undefined &&
          value !== null &&
          (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean')
        ) {
          filtered[key] = String(value);
        }
      }
      if (Object.keys(filtered).length > 0) {
        url += `?${new URLSearchParams(filtered).toString()}`;
      }
    }

    return url;
  }

  private isTransient(error: Error): boolean {
    // Deliberately cancelled (cancelInFlight) — the caller already moved on,
    // never retry.
    if (error instanceof RequestCancelledError) {
      return false;
    }
    if (error instanceof NetworkError || error instanceof TimeoutError) {
      return true;
    }
    if (error instanceof APIError) {
      return error.status >= 500 || error.status === 429;
    }
    // Everything else — including MissingCredentialError — is a client-side
    // defect. Re-issuing the same request cannot change the answer, and retrying
    // would only delay and blur the loud failure.
    return false;
  }
}
